import React, { useState, useRef, useEffect } from 'react';
import { ArrowUp } from 'lucide-react';
import { cn } from '@/utils/cn';

interface ChatInputProps {
  onSend: (message: string) => void;
  isLoading: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({ onSend, isLoading }) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 5 * 24;
      const newHeight = Math.min(scrollHeight, maxHeight);
      textareaRef.current.style.height = `${newHeight}px`;
      textareaRef.current.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
    }
  }, [message]);

  const handleSubmit = () => {
    const trimmed = message.trim();
    if (!trimmed || isLoading) return;
    onSend(trimmed);
    setMessage('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Message your co-work assistant..."
        disabled={isLoading}
        rows={1}
        className={cn(
          'w-full resize-none pl-4 pr-12 py-3 text-sm rounded-xl',
          'border-2 border-[#1a1a2e] focus:ring-2 focus:ring-violet-300 focus:outline-none',
          'placeholder:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed',
          'bg-white shadow-[2px_2px_0px_#1a1a2e]'
        )}
      />
      <button
        onClick={handleSubmit}
        disabled={!message.trim() || isLoading}
        className={cn(
          'absolute right-2 bottom-2 p-1.5 rounded-lg',
          'transition-all duration-150',
          message.trim() && !isLoading
            ? 'bg-violet-500 text-white border-2 border-[#1a1a2e] shadow-[1px_1px_0px_#1a1a2e] hover:shadow-none hover:translate-x-[0.5px] hover:translate-y-[0.5px]'
            : 'bg-gray-100 text-gray-400 border border-gray-200'
        )}
      >
        <ArrowUp className="w-4 h-4" />
      </button>
    </div>
  );
};
